<script setup lang="ts">
import type { DropZoneData } from '@/types/components/DropZoneData'
import { EHubProvider } from '@/types/enum/HubProviderEnum'
import type { CheckpointModel } from '@/types/models/CheckpointModel'
import type { AssociatedDeviceModel, DeviceModel } from '@/types/models/DeviceModel'
import type { PartnerModel } from '@/types/partners/PartnerModel'
import type { PermissionsEnums } from '@/types/permissions/PermissionsEnum'
import type { SocketCheckpointDetectionModel } from '@/types/socket/SocketDetectionModel'
import { StackLayout } from '@rfc/rfc-components-library'
import type { Position } from '@vueuse/core'
import { computed, inject, ref } from 'vue'
import CheckpointCard from './components/CheckpointCard.vue'
import CheckpointOrderTable from './components/CheckpointOrderTable.vue'

type Props = {
    sync?: boolean
    partnerConfig?: PartnerModel
    data?: CheckpointModel[]
    associatedDevices?: Partial<DeviceModel>[]
    detections?: SocketCheckpointDetectionModel[]
    editPermission: PermissionsEnums
    deletePermission: PermissionsEnums
    dropZone?: DropZoneData
}
const props = defineProps<Props>()

type Emits = {
    (e: 'refresh'): void
    (e: 'goTo', value?: CheckpointModel): void
    (e: 'delete', checkpoint: CheckpointModel): void
    (e: 'dragStart', device?: AssociatedDeviceModel, checkpointId?: number): void
    (e: 'dragEnd', position?: Position): void
    (e: 'close'): void
    (e: 'setCheckpointsOrder', value: number[]): void
}
defineEmits<Emits>()

const orderEdit = inject('orderEdit', false)
const isViewerOnly = inject<boolean>(EHubProvider.IS_VIEWER_ONLY, false)

const stackRef = ref<{ stackRef: HTMLDivElement }>()
const stackWidth = computed(() => stackRef.value?.stackRef.getBoundingClientRect().width)
</script>

<template>
    <StackLayout
        ref="stackRef"
        v-if="data?.length"
        :class="`flex-wrap checkpoint-carousel w-full`"
        isResponsiveSmallScreen
        :direction="orderEdit ? 'column' : 'row'"
        :gap="16">
        <CheckpointOrderTable
            v-if="!isViewerOnly && orderEdit"
            :data="data"
            @setCheckpointsOrder="$emit('setCheckpointsOrder', $event)"
            class="overflow-y-clip h-full" />
        <template v-else>
            <CheckpointCard
                v-for="(checkpoint, key) in data"
                :key="key"
                :parentWidth="stackWidth"
                :data="checkpoint"
                :associatedDevices="associatedDevices"
                :detections="
                    detections?.find(item => item.checkpoint_id === checkpoint.id)?.detections ?? []
                "
                :editPermission="editPermission"
                :deletePermission="deletePermission"
                :dropZoneData="dropZone"
                :partnerConfig="partnerConfig"
                @dragStart="(val1, val2) => $emit('dragStart', val1, val2)"
                @dragEnd="$emit('dragEnd', $event)"
                @delete="$emit('delete', $event)"
                @click="$emit('goTo', $event)"
                @close="$emit('close')"
                @refresh="$emit('refresh')" />
        </template>
    </StackLayout>

    <StackLayout v-else class="align-self-center no-record">
        {{ $t('common.noRecord') }}
    </StackLayout>
</template>

<style scoped lang="scss">
.checkpoint-carousel {
    padding-bottom: 8px;

    & :deep(.checkpoint-dropdown) {
        cursor: default;
        min-width: 18%;

        flex: 1 0 auto;
        flex-basis: min-content;

        & .card-content {
            pointer-events: none;
            align-self: center;
        }
        & .dropzone-card {
            & .card-footer {
                pointer-events: none;
            }
        }

        &.flex-auto {
            flex: auto;
        }
    }
}

.device-drawer {
    height: 100dvh;
    padding: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.drawer-details-header {
    padding: 0.7rem 1rem;
    border-bottom: 1px solid grey;
    position: sticky;
    top: 0;
    width: 100%;
    background-color: var(--color-background-secondary);
}

.drawer-details-content {
    flex-grow: 1;
    overflow-y: auto;
    padding: 1rem;
}
</style>
